import { useMemo } from 'react'
import { Zap, TrendingUp, DollarSign, Target, BarChart3 } from 'lucide-react'
import { formatCurrency, formatCurrencyCompact } from '@/utils/format-currency'
import type { Deal, DealStage } from '@/types'

interface MonthlyMetric {
  month: string
  total: number
  won: number
  forecast: number
}

function calcProbability(stage: DealStage): number {
  const map: Record<DealStage, number> = {
    qualificado: 10,
    reuniao_agendada: 20,
    reuniao_realizada: 30,
    proposta_elaboracao: 40,
    proposta_enviada: 50,
    teste_gratis: 60,
    renegociacao: 45,
    gerar_contrato: 65,
    contrato_enviado: 75,
    ajustar_proposta: 55,
    handover: 90,
    ganho: 100,
    perdido: 0,
    pausado: 0,
    fechamento_futuro: 15,
  }
  return map[stage] ?? 0
}

function generateMonthlyMetrics(deals: Deal[]): MonthlyMetric[] {
  const months: MonthlyMetric[] = []
  const now = new Date()
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const label = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
    const monthDeals = deals.filter((deal) => {
      const created = new Date(deal.createdAt)
      return created.getMonth() === d.getMonth() && created.getFullYear() === d.getFullYear()
    })
    months.push({
      month: label,
      total: monthDeals.reduce((acc, deal) => acc + (deal.valor || 0), 0),
      won: monthDeals
        .filter((deal) => deal.etapa === 'ganho')
        .reduce((acc, deal) => acc + (deal.valor || 0), 0),
      forecast: monthDeals
        .filter((deal) => deal.etapa !== 'perdido' && deal.etapa !== 'ganho')
        .reduce((acc, deal) => acc + ((deal.valor || 0) * calcProbability(deal.etapa)) / 100, 0),
    })
  }
  return months
}

interface RocketMetricsPanelProps {
  deals: Deal[]
  closerFilter?: string
  className?: string
}

export function RocketMetricsPanel({ deals, closerFilter, className = '' }: RocketMetricsPanelProps) {
  const filteredDeals = useMemo(() => {
    if (!closerFilter) return deals
    return deals.filter((d) => d.closerId === closerFilter || d.closerNome === closerFilter)
  }, [deals, closerFilter])

  const metrics = useMemo(() => {
    const activeDeals = filteredDeals.filter((d) => d.etapa !== 'perdido' && d.etapa !== 'ganho')
    const wonDeals = filteredDeals.filter((d) => d.etapa === 'ganho')
    const pipelineTotal = activeDeals.reduce((acc, d) => acc + (d.valor || 0), 0)
    const weightedForecast = activeDeals.reduce(
      (acc, d) => acc + ((d.valor || 0) * calcProbability(d.etapa)) / 100,
      0,
    )
    const avgProbability =
      activeDeals.length > 0
        ? activeDeals.reduce((acc, d) => acc + calcProbability(d.etapa), 0) / activeDeals.length
        : 0

    return {
      activeDeals: activeDeals.length,
      wonDeals: wonDeals.length,
      pipelineTotal,
      weightedForecast,
      avgProbability,
      wonValue: wonDeals.reduce((acc, d) => acc + (d.valor || 0), 0),
    }
  }, [filteredDeals])

  const monthlyMetrics = useMemo(() => generateMonthlyMetrics(filteredDeals), [filteredDeals])
  const maxMonthlyValue = Math.max(...monthlyMetrics.map((m) => m.total), 1)

  return (
    <div className={`space-y-5 ${className}`.trim()}>
      <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
        <div className='glass-card space-y-1 p-4'>
          <div className='flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--font-color-muted)]'>
            <Zap size={12} className='text-[var(--brand-primary)]' />
            Pipeline Total
          </div>
          <p className='text-lg font-bold text-[var(--font-color-h1)]'>
            {formatCurrencyCompact(metrics.pipelineTotal)}
          </p>
          <p className='text-[11px] text-[var(--font-color-muted)]'>
            {metrics.activeDeals} deals ativos
          </p>
        </div>

        <div className='glass-card space-y-1 p-4'>
          <div className='flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--font-color-muted)]'>
            <TrendingUp size={12} className='text-emerald-400' />
            Forecast
          </div>
          <p className='text-lg font-bold text-[var(--font-color-h1)]'>
            {formatCurrencyCompact(metrics.weightedForecast)}
          </p>
          <p className='text-[11px] text-[var(--font-color-muted)]'>
            Valor ponderado por probabilidade
          </p>
        </div>

        <div className='glass-card space-y-1 p-4'>
          <div className='flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--font-color-muted)]'>
            <Target size={12} className='text-purple-400' />
            Probabilidade Média
          </div>
          <p className='text-lg font-bold text-[var(--font-color-h1)]'>
            {metrics.avgProbability.toFixed(0)}%
          </p>
          <p className='text-[11px] text-[var(--font-color-muted)]'>
            Média entre deals ativos
          </p>
        </div>

        <div className='glass-card space-y-1 p-4'>
          <div className='flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[var(--font-color-muted)]'>
            <DollarSign size={12} className='text-emerald-400' />
            Fechados (Ganhos)
          </div>
          <p className='text-lg font-bold text-emerald-400'>
            {formatCurrencyCompact(metrics.wonValue)}
          </p>
          <p className='text-[11px] text-[var(--font-color-muted)]'>
            {metrics.wonDeals} deals ganhos
          </p>
        </div>
      </div>

      <div className='glass-card p-5'>
        <div className='mb-4 flex items-center gap-2'>
          <BarChart3 size={15} className='text-[var(--brand-primary)]' />
          <h3 className='text-sm font-semibold text-[var(--font-color-h3)]'>
            Performance Mensal (6 meses)
          </h3>
        </div>
        <div className='grid h-32 grid-cols-6 items-end gap-2'>
          {monthlyMetrics.map((m, i) => (
            <div key={i} className='flex h-full flex-col items-center justify-end gap-1'>
              <span className='text-[10px] text-[var(--font-color-muted)]'>
                {formatCurrencyCompact(m.total)}
              </span>
              <div
                className='flex w-full items-end gap-0.5'
                style={{ height: `${Math.max((m.total / maxMonthlyValue) * 100, 4)}%` }}
              >
                <div
                  className='min-h-[4px] flex-1 rounded-t-sm bg-[var(--brand-primary)]/60'
                  style={{ height: `${(m.total / maxMonthlyValue) * 100}%` }}
                  title={`Total: ${formatCurrency(m.total)}`}
                />
                <div
                  className='min-h-[4px] flex-1 rounded-t-sm bg-emerald-400/60'
                  style={{ height: `${(m.won / maxMonthlyValue) * 100}%` }}
                  title={`Ganhos: ${formatCurrency(m.won)}`}
                />
              </div>
              <span className='mt-1 text-[10px] font-medium text-[var(--font-color-muted)]'>
                {m.month}
              </span>
            </div>
          ))}
        </div>
        <div className='mt-3 flex items-center gap-4 text-[10px] text-[var(--font-color-muted)]'>
          <div className='flex items-center gap-1.5'>
            <div className='h-2.5 w-2.5 rounded-sm bg-[var(--brand-primary)]/60' />
            Total Pipeline
          </div>
          <div className='flex items-center gap-1.5'>
            <div className='h-2.5 w-2.5 rounded-sm bg-emerald-400/60' />
            Ganhos
          </div>
        </div>
      </div>
    </div>
  )
}
